import { DEFAULT_DEVICE_ID } from "../devices/catalog";
import { type ChartKind, sampleChartData } from "./chart";
import {
  type Box,
  boundingRect,
  boxCenter,
  buildParentMap,
  type ElementParent,
  elementBox,
  rotatePoint,
  toGroupSpace,
  toParentSpace,
  unionRects,
} from "./geometry";
import { maskForShape } from "./masks";
import { boardRects, freeBoardSpot, PAGE_MAX, PAGE_MIN, pageSize, supportsBoard } from "./pages";
import { scaleTextMarks } from "./richtext";
import type {
  ChartElement,
  DeviceElement,
  GroupElement,
  IconElement,
  IconPath,
  IconWeight,
  ImageElement,
  Paint,
  PathElement,
  QrElement,
  SceneDocument,
  SceneElement,
  ScenePage,
  ShapeElement,
  ShapeKind,
  SvgElement,
  TextElement,
  TextStyle,
} from "./schema";
import { chartElementSchema } from "./schema";

/**
 * Structural document operations. They mutate the document in place, so they
 * work on plain objects as well as on Immer drafts (editor) and are shared
 * with the backend (MCP tools) and the tests.
 */

/** Deep copy of JSON data (documents are plain JSON; works on Immer drafts too). */
export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

let counter = 0;
export function createId(prefix: string): string {
  counter = (counter + 1) % 1_679_616;
  const random = Math.random().toString(36).slice(2, 7);
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${random}`;
}

// ---------------------------------------------------------------------------
// Element factories
// ---------------------------------------------------------------------------

const base = { rotation: 0, opacity: 1, locked: false, hidden: false } as const;

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: "Inter",
  fontSize: 48,
  fontWeight: 400,
  italic: false,
  underline: false,
  color: "#111111",
  align: "left",
  verticalAlign: "top",
  lineHeight: 1.25,
  letterSpacing: 0,
  uppercase: false,
};

export function createTextElement(
  input: Partial<Omit<TextElement, "style" | "type">> & { style?: Partial<TextStyle> },
): TextElement {
  const style = { ...DEFAULT_TEXT_STYLE, ...input.style };
  return {
    ...base,
    id: input.id ?? createId("text"),
    type: "text",
    x: 0,
    y: 0,
    width: 400,
    height: Math.ceil(style.fontSize * style.lineHeight),
    text: "Text",
    autoHeight: true,
    ...input,
    style,
  };
}

export function createShapeElement(
  shape: ShapeKind,
  input: Partial<Omit<ShapeElement, "type" | "shape">> = {},
): ShapeElement {
  return {
    ...base,
    id: input.id ?? createId("shape"),
    type: "shape",
    shape,
    x: 0,
    y: 0,
    width: 200,
    height: shape === "line" ? 8 : 200,
    fill: "#94a3b8",
    radius: 0,
    sides: shape === "star" ? 5 : 6,
    innerRatio: 0.5,
    ...(shape === "line" ? { stroke: { color: "#111111", width: 4, dash: "solid" as const } } : {}),
    ...input,
  };
}

export function createImageElement(
  assetId: string,
  input: Partial<Omit<ImageElement, "type" | "assetId">> = {},
): ImageElement {
  return {
    ...base,
    id: input.id ?? createId("img"),
    type: "image",
    assetId,
    x: 0,
    y: 0,
    width: 400,
    height: 300,
    fit: "cover",
    crop: null,
    radius: 0,
    flipX: false,
    flipY: false,
    ...input,
  };
}

export function createSvgElement(
  assetId: string,
  input: Partial<Omit<SvgElement, "type" | "assetId">> = {},
): SvgElement {
  return {
    ...base,
    id: input.id ?? createId("svg"),
    type: "svg",
    assetId,
    x: 0,
    y: 0,
    width: 300,
    height: 300,
    flipX: false,
    flipY: false,
    ...input,
  };
}

export function createQrElement(
  value: string,
  input: Partial<Omit<QrElement, "type" | "value">> = {},
): QrElement {
  return {
    ...base,
    id: input.id ?? createId("qr"),
    type: "qr",
    value,
    x: 0,
    y: 0,
    width: 300,
    height: 300,
    color: "#111111",
    background: "#ffffff",
    ecc: "M",
    margin: 2,
    style: "square",
    ...input,
  };
}

/** Icon from the library; `paths` come from the catalog (`loadIconPaths`). */
export function createIconElement(
  icon: string,
  weight: IconWeight,
  paths: IconPath[],
  input: Partial<Omit<IconElement, "type" | "icon" | "weight" | "paths">> = {},
): IconElement {
  return {
    ...base,
    id: input.id ?? createId("icon"),
    type: "icon",
    icon,
    weight,
    paths: paths.map((path) => ({ ...path })),
    x: 0,
    y: 0,
    width: 200,
    height: 200,
    color: "#1a1a1a",
    flipX: false,
    flipY: false,
    ...input,
  };
}

/** Device mockup; the caller sizes the box to the device (`deviceAspect`). */
export function createDeviceElement(
  input: Partial<Omit<DeviceElement, "type">> = {},
): DeviceElement {
  return {
    ...base,
    id: input.id ?? createId("device"),
    type: "device",
    device: DEFAULT_DEVICE_ID,
    x: 0,
    y: 0,
    width: 300,
    height: 600,
    landscape: false,
    tilt: { x: 0, y: 0, z: 0 },
    fit: "cover",
    glare: false,
    ...input,
  };
}

/** Freehand stroke (drawing tool); `d` is relative to `viewBox` (see scene/path.ts). */
export function createPathElement(
  input: Partial<Omit<PathElement, "type">> & Pick<PathElement, "d" | "viewBox">,
): PathElement {
  return {
    ...base,
    id: input.id ?? createId("path"),
    type: "path",
    x: 0,
    y: 0,
    width: input.viewBox[0],
    height: input.viewBox[1],
    color: "#1a1a1a",
    strokeWidth: 4,
    brush: "pen",
    ...input,
  };
}

/** Chart with sample data for its kind (scene/chart.ts). */
export function createChartElement(
  kind: ChartKind,
  input: Partial<Omit<ChartElement, "type">> = {},
): ChartElement {
  const sample = sampleChartData(kind);
  const defaults = chartElementSchema.parse({
    ...base,
    id: "chart",
    type: "chart",
    x: 0,
    y: 0,
    width: kind === "pie" || kind === "donut" || kind === "progress" ? 560 : 800,
    height: kind === "progress" ? 560 : 520,
    kind,
    title: sample.title,
    unit: sample.unit,
    labels: sample.labels,
    series: sample.series,
    legend: kind === "progress" || kind === "bar" ? "none" : "bottom",
    showValues: kind === "pie" || kind === "donut" || kind === "bar",
    gridlines: kind !== "bar",
  });
  return { ...defaults, ...input, id: input.id ?? createId("chart"), type: "chart" };
}

/** Is the element an image placeholder (from a template)? */
export function isImagePlaceholder(
  element: SceneElement | undefined,
): element is ShapeElement & { placeholder: "image" } {
  return element?.type === "shape" && element.placeholder === "image";
}

/**
 * The image that takes the place of a placeholder: same id, box, rounding,
 * frame, effects and timing, so it sits exactly where the template put it.
 */
export function imageFromPlaceholder(shape: ShapeElement, assetId: string): ImageElement {
  const mask = shape.mask ? { ...shape.mask } : maskForShape(shape);
  const brand = {
    ...(shape.brand?.radius ? { radius: shape.brand.radius } : {}),
    ...(shape.brand?.stroke && shape.stroke ? { stroke: shape.brand.stroke } : {}),
  };
  return {
    id: shape.id,
    type: "image",
    assetId,
    ...(shape.name && shape.name !== "Bild" ? { name: shape.name } : {}),
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
    rotation: shape.rotation,
    opacity: shape.opacity,
    locked: shape.locked,
    hidden: shape.hidden,
    fit: "cover",
    crop: null,
    // Ellipse / triangle / polygon / star placeholders keep their outline as mask.
    radius: mask ? 0 : shape.radius,
    ...(mask ? { mask } : {}),
    flipX: false,
    flipY: false,
    ...(shape.stroke ? { stroke: { ...shape.stroke } } : {}),
    ...(shape.shadow ? { shadow: { ...shape.shadow } } : {}),
    ...(shape.time ? { time: { ...shape.time } } : {}),
    // Plain copy: `shape` may be an Immer draft.
    ...(shape.animation ? { animation: JSON.parse(JSON.stringify(shape.animation)) } : {}),
    ...(Object.keys(brand).length > 0 ? { brand } : {}),
  };
}

/** Fits a natural size into a page, keeping the aspect ratio. */
export function fitSize(
  natural: { width: number; height: number },
  max: { width: number; height: number },
): { width: number; height: number } {
  const ratio = Math.min(max.width / natural.width, max.height / natural.height, 1);
  return {
    width: Math.max(1, Math.round(natural.width * ratio)),
    height: Math.max(1, Math.round(natural.height * ratio)),
  };
}

// ---------------------------------------------------------------------------
// Parents & lists
// ---------------------------------------------------------------------------

export function childList(doc: SceneDocument, parent: ElementParent): string[] | null {
  if (parent.kind === "page") return doc.pages[parent.pageIndex]?.children ?? null;
  const group = doc.elements[parent.groupId];
  return group?.type === "group" ? group.children : null;
}

function subtreeIds(doc: SceneDocument, id: string, out: string[] = [], depth = 0): string[] {
  const element = doc.elements[id];
  if (!element || depth > 50) return out;
  out.push(id);
  if (element.type === "group") {
    for (const child of element.children) subtreeIds(doc, child, out, depth + 1);
  }
  return out;
}

/** Adds top-level elements to a page (by default on top). */
export function insertElements(
  doc: SceneDocument,
  pageIndex: number,
  elements: SceneElement[],
  index?: number,
): void {
  const page = doc.pages[pageIndex];
  if (!page) throw new Error(`Seite ${pageIndex + 1} existiert nicht`);
  for (const element of elements) doc.elements[element.id] = element;
  const ids = elements.map((element) => element.id);
  if (index === undefined || index >= page.children.length) page.children.push(...ids);
  else page.children.splice(Math.max(0, index), 0, ...ids);
}

/** Removes elements including all descendants. Empty groups disappear as well. */
export function removeElements(doc: SceneDocument, ids: string[]): void {
  const parents = buildParentMap(doc);
  const touchedGroups = new Set<string>();
  for (const id of ids) {
    if (!doc.elements[id]) continue;
    const parent = parents.get(id);
    if (parent) {
      const list = childList(doc, parent);
      const position = list?.indexOf(id) ?? -1;
      if (list && position >= 0) list.splice(position, 1);
      if (parent.kind === "group") touchedGroups.add(parent.groupId);
    }
    for (const descendant of subtreeIds(doc, id)) delete doc.elements[descendant];
  }
  for (const groupId of touchedGroups) {
    const group = doc.elements[groupId];
    if (group?.type !== "group") continue;
    if (group.children.length === 0) removeElements(doc, [groupId]);
    else normalizeGroup(doc, groupId);
  }
}

export interface ClipboardData {
  /** Top-level element ids in paint order. */
  roots: string[];
  elements: Record<string, SceneElement>;
}

/** Copies elements (with descendants) into a self-contained structure. */
export function copyElements(doc: SceneDocument, ids: string[]): ClipboardData {
  const parents = buildParentMap(doc);
  const roots: string[] = [];
  const elements: Record<string, SceneElement> = {};
  for (const id of ids) {
    const element = doc.elements[id];
    if (!element) continue;
    // Copy grouped elements with their page-space box.
    const parent = parents.get(id);
    let box = elementBox(element);
    let current = parent;
    while (current?.kind === "group") {
      const group = doc.elements[current.groupId];
      if (!group) break;
      box = toParentSpace(box, elementBox(group));
      current = parents.get(group.id);
    }
    roots.push(id);
    for (const descendant of subtreeIds(doc, id)) {
      elements[descendant] = clone(doc.elements[descendant]!);
    }
    Object.assign(elements[id]!, { x: box.x, y: box.y, rotation: box.rotation });
  }
  return { roots, elements };
}

/**
 * Inserts copied elements with fresh ids on a page. Returns the new root ids.
 */
export function pasteElements(
  doc: SceneDocument,
  pageIndex: number,
  data: ClipboardData,
  offset = { x: 0, y: 0 },
): string[] {
  const idMap = new Map<string, string>();
  for (const oldId of Object.keys(data.elements)) {
    const prefix = oldId.split("_")[0] || "el";
    idMap.set(oldId, createId(prefix));
  }
  const newRoots: string[] = [];
  for (const [oldId, element] of Object.entries(data.elements)) {
    const copy = clone(element);
    copy.id = idMap.get(oldId)!;
    if (copy.type === "group")
      copy.children = copy.children.map((child: string) => idMap.get(child)!);
    doc.elements[copy.id] = copy;
  }
  for (const root of data.roots) {
    const id = idMap.get(root);
    if (!id) continue;
    const element = doc.elements[id]!;
    element.x += offset.x;
    element.y += offset.y;
    newRoots.push(id);
  }
  const page = doc.pages[pageIndex];
  if (!page) throw new Error(`Seite ${pageIndex + 1} existiert nicht`);
  page.children.push(...newRoots);
  return newRoots;
}

/** Duplicates elements next to the originals (same parent, above them). */
export function duplicateElements(
  doc: SceneDocument,
  ids: string[],
  offset = { x: 20, y: 20 },
): string[] {
  const parents = buildParentMap(doc);
  const result: string[] = [];
  for (const id of ids) {
    const parent = parents.get(id);
    const list = parent ? childList(doc, parent) : null;
    if (!parent || !list) continue;
    const data: ClipboardData = { roots: [id], elements: {} };
    for (const descendant of subtreeIds(doc, id)) {
      data.elements[descendant] = clone(doc.elements[descendant]!);
    }
    const idMap = new Map<string, string>();
    for (const oldId of Object.keys(data.elements)) {
      idMap.set(oldId, createId(oldId.split("_")[0] || "el"));
    }
    for (const [oldId, element] of Object.entries(data.elements)) {
      const copy = clone(element);
      copy.id = idMap.get(oldId)!;
      if (copy.type === "group")
        copy.children = copy.children.map((child: string) => idMap.get(child)!);
      doc.elements[copy.id] = copy;
    }
    const newId = idMap.get(id)!;
    const copy = doc.elements[newId]!;
    copy.x += offset.x;
    copy.y += offset.y;
    list.splice(list.indexOf(id) + 1, 0, newId);
    if (parent.kind === "group") normalizeGroup(doc, parent.groupId);
    result.push(newId);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

/**
 * Groups elements that share the same parent. Returns the group id, or null
 * when fewer than two suitable elements were given.
 */
export function groupElements(doc: SceneDocument, ids: string[]): string | null {
  const parents = buildParentMap(doc);
  const unique = [...new Set(ids)].filter((id) => doc.elements[id]);
  if (unique.length < 2) return null;
  const parent = parents.get(unique[0]!);
  if (!parent) return null;
  const sameParent = unique.every((id) => {
    const other = parents.get(id);
    if (!other || other.kind !== parent.kind) return false;
    return other.kind === "page"
      ? parent.kind === "page" && other.pageIndex === parent.pageIndex
      : parent.kind === "group" && other.groupId === parent.groupId;
  });
  if (!sameParent) return null;
  const list = childList(doc, parent)!;
  const ordered = list.filter((id) => unique.includes(id));
  const bounds = unionRects(ordered.map((id) => boundingRect(elementBox(doc.elements[id]!))))!;
  const group: GroupElement = {
    ...base,
    id: createId("group"),
    type: "group",
    x: bounds.x,
    y: bounds.y,
    width: Math.max(1, bounds.width),
    height: Math.max(1, bounds.height),
    children: ordered,
  };
  for (const id of ordered) {
    const element = doc.elements[id]!;
    element.x -= bounds.x;
    element.y -= bounds.y;
  }
  // Video: a group of timed elements spans their times (one bar on the timeline).
  const ranges = ordered.map((id) => doc.elements[id]!.time);
  if (ranges.length > 0 && ranges.every(Boolean)) {
    group.time = {
      start: Math.min(...ranges.map((range) => range!.start)),
      end: Math.max(...ranges.map((range) => range!.end)),
    };
  }
  const insertAt = list.indexOf(ordered[ordered.length - 1]!);
  doc.elements[group.id] = group;
  list.splice(insertAt + 1, 0, group.id);
  for (const id of ordered) list.splice(list.indexOf(id), 1);
  if (parent.kind === "group") normalizeGroup(doc, parent.groupId);
  return group.id;
}

/** Dissolves a group; its children keep their visual position. Returns their ids. */
export function ungroupElement(doc: SceneDocument, groupId: string): string[] {
  const group = doc.elements[groupId];
  if (group?.type !== "group") return [];
  const parents = buildParentMap(doc);
  const parent = parents.get(groupId);
  const list = parent ? childList(doc, parent) : null;
  if (!parent || !list) return [];
  const groupBox = elementBox(group);
  for (const childId of group.children) {
    const child = doc.elements[childId];
    if (!child) continue;
    const box = toParentSpace(elementBox(child), groupBox);
    Object.assign(child, {
      x: box.x,
      y: box.y,
      rotation: box.rotation,
      opacity: (child.opacity ?? 1) * (group.opacity ?? 1),
      hidden: child.hidden || group.hidden,
      locked: child.locked || group.locked,
    });
    // Video: the group limited when its content was visible; the elements keep that.
    if (group.time) {
      const start = Math.max(group.time.start, child.time?.start ?? group.time.start);
      const end = Math.min(group.time.end, child.time?.end ?? group.time.end);
      child.time = end > start ? { start, end } : { ...group.time };
    }
  }
  const index = list.indexOf(groupId);
  list.splice(index, 1, ...group.children);
  const children = [...group.children];
  delete doc.elements[groupId];
  if (parent.kind === "group") normalizeGroup(doc, parent.groupId);
  return children;
}

/**
 * Recomputes a group's box from its children (after a child moved) while
 * keeping everything visually in place.
 */
export function normalizeGroup(doc: SceneDocument, groupId: string): void {
  const group = doc.elements[groupId];
  if (group?.type !== "group") return;
  const bounds = unionRects(
    group.children.flatMap((id) => {
      const child = doc.elements[id];
      return child ? [boundingRect(elementBox(child))] : [];
    }),
  );
  if (!bounds) return;
  const epsilon = 0.001;
  if (
    Math.abs(bounds.x) < epsilon &&
    Math.abs(bounds.y) < epsilon &&
    Math.abs(bounds.width - group.width) < epsilon &&
    Math.abs(bounds.height - group.height) < epsilon
  ) {
    return;
  }
  const oldBox = elementBox(group);
  // New group rectangle, expressed in the group's local space, mapped to its parent.
  const localCenter = {
    x: group.x + bounds.x + bounds.width / 2,
    y: group.y + bounds.y + bounds.height / 2,
  };
  const center = rotatePoint(localCenter, boxCenter(oldBox), group.rotation);
  group.x = center.x - bounds.width / 2;
  group.y = center.y - bounds.height / 2;
  group.width = Math.max(1, bounds.width);
  group.height = Math.max(1, bounds.height);
  for (const id of group.children) {
    const child = doc.elements[id];
    if (!child) continue;
    child.x -= bounds.x;
    child.y -= bounds.y;
  }
  const parents = buildParentMap(doc);
  const parent = parents.get(groupId);
  if (parent?.kind === "group") normalizeGroup(doc, parent.groupId);
}

/** Moves a box of an element (given in page space) back into its own parent space. */
export function setWorldBox(doc: SceneDocument, elementId: string, box: Box): void {
  const element = doc.elements[elementId];
  if (!element) return;
  const parents = buildParentMap(doc);
  const chain: string[] = [];
  let parent = parents.get(elementId);
  while (parent?.kind === "group") {
    chain.unshift(parent.groupId);
    parent = parents.get(parent.groupId);
  }
  let local = box;
  // Walk down from the outermost group, converting page space → group space.
  let parentBox: Box | null = null;
  for (const groupId of chain) {
    const group = doc.elements[groupId]!;
    const groupBox: Box = parentBox
      ? toParentSpace(elementBox(group), parentBox)
      : elementBox(group);
    parentBox = groupBox;
  }
  if (parentBox) local = toGroupSpace(box, parentBox);
  element.x = local.x;
  element.y = local.y;
  element.width = Math.max(1, local.width);
  element.height = Math.max(1, local.height);
  element.rotation = local.rotation;
  if (chain.length > 0) normalizeGroup(doc, chain[chain.length - 1]!);
}

/**
 * Scales an element's content uniformly (used when resizing groups or
 * multi-selections). Position is not touched; children of groups are
 * scaled around the group origin.
 */
export function scaleContent(doc: SceneDocument, elementId: string, factor: number): void {
  const element = doc.elements[elementId];
  if (!element || !Number.isFinite(factor) || factor <= 0) return;
  element.width = Math.max(1, element.width * factor);
  element.height = Math.max(1, element.height * factor);
  if (element.shadow) {
    element.shadow.x *= factor;
    element.shadow.y *= factor;
    element.shadow.blur *= factor;
  }
  switch (element.type) {
    case "text":
      element.style.fontSize = Math.max(1, element.style.fontSize * factor);
      if (element.style.stroke) element.style.stroke.width *= factor;
      scaleTextMarks(element, factor);
      break;
    case "shape":
      element.radius *= factor;
      if (element.stroke) element.stroke.width *= factor;
      break;
    case "image":
      element.radius *= factor;
      if (element.stroke) element.stroke.width *= factor;
      break;
    case "group":
      for (const childId of element.children) {
        const child = doc.elements[childId];
        if (!child) continue;
        child.x *= factor;
        child.y *= factor;
        scaleContent(doc, childId, factor);
      }
      break;
    case "chart":
      element.fontSize = Math.max(4, Math.min(400, element.fontSize * factor));
      element.lineWidth = Math.max(0.5, Math.min(60, element.lineWidth * factor));
      element.barRadius = Math.min(500, element.barRadius * factor);
      break;
    case "table":
      element.fontSize = Math.min(500, element.fontSize * factor);
      element.padding = Math.min(200, element.padding * factor);
      element.borderWidth = Math.min(50, element.borderWidth * factor);
      element.rowHeight = Math.min(2000, element.rowHeight * factor);
      break;
    case "svg":
    case "qr":
    case "icon":
    case "device":
    case "path":
      break;
  }
}

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

export type ReorderMode = "forward" | "backward" | "front" | "back";

/** Changes the paint order of elements within their parent. */
export function reorderElements(doc: SceneDocument, ids: string[], mode: ReorderMode): void {
  const parents = buildParentMap(doc);
  const byList = new Map<string[], string[]>();
  for (const id of ids) {
    const parent = parents.get(id);
    const list = parent ? childList(doc, parent) : null;
    if (!list) continue;
    byList.set(list, [...(byList.get(list) ?? []), id]);
  }
  for (const [list, selected] of byList) {
    const set = new Set(selected);
    if (mode === "front" || mode === "back") {
      const rest = list.filter((id) => !set.has(id));
      const moved = list.filter((id) => set.has(id));
      list.splice(
        0,
        list.length,
        ...(mode === "front" ? [...rest, ...moved] : [...moved, ...rest]),
      );
      continue;
    }
    if (mode === "forward") {
      for (let i = list.length - 2; i >= 0; i--) {
        if (set.has(list[i]!) && !set.has(list[i + 1]!)) {
          [list[i], list[i + 1]] = [list[i + 1]!, list[i]!];
        }
      }
    } else {
      for (let i = 1; i < list.length; i++) {
        if (set.has(list[i]!) && !set.has(list[i - 1]!)) {
          [list[i], list[i - 1]] = [list[i - 1]!, list[i]!];
        }
      }
    }
  }
}

/** Moves one element to a position within its current parent list. */
export function moveElementTo(doc: SceneDocument, elementId: string, index: number): void {
  const parent = buildParentMap(doc).get(elementId);
  const list = parent ? childList(doc, parent) : null;
  if (!list) return;
  const from = list.indexOf(elementId);
  if (from < 0) return;
  list.splice(from, 1);
  list.splice(Math.max(0, Math.min(index, list.length)), 0, elementId);
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

/**
 * Inserts an empty page at `index`. `size` gives it its own size (designs without
 * print format; the same as the document's size means no own size). On a board it
 * lands on a free spot right of the previous page.
 */
export function addPage(
  doc: SceneDocument,
  index: number,
  background: Paint = "#ffffff",
  size?: { width: number; height: number },
): string {
  const at = Math.max(0, Math.min(index, doc.pages.length));
  const page: ScenePage = { id: createId("page"), background, children: [] };
  if (size && supportsBoard(doc)) {
    if (size.width !== doc.format.width || size.height !== doc.format.height) {
      page.width = Math.round(size.width);
      page.height = Math.round(size.height);
    }
  }
  if (doc.board && supportsBoard(doc)) {
    Object.assign(page, freeBoardSpot(doc, at - 1, pageSize(doc, page)));
  }
  doc.pages.splice(at, 0, page);
  return page.id;
}

/**
 * Gives a page a new size. With `scale` the content grows or shrinks with it
 * (uniformly by the smaller ratio, every element keeps its relative position);
 * otherwise it stays where it is.
 */
export function resizePage(
  doc: SceneDocument,
  pageIndex: number,
  size: { width: number; height: number },
  scale = false,
): void {
  const page = doc.pages[pageIndex];
  if (!page || !supportsBoard(doc)) return;
  const old = pageSize(doc, page);
  const width = Math.max(PAGE_MIN, Math.min(PAGE_MAX, Math.round(size.width)));
  const height = Math.max(PAGE_MIN, Math.min(PAGE_MAX, Math.round(size.height)));
  if (width === old.width && height === old.height) return;
  if (scale) {
    const sx = width / old.width;
    const sy = height / old.height;
    const factor = Math.min(sx, sy);
    for (const id of page.children) {
      const element = doc.elements[id];
      if (!element) continue;
      const cx = (element.x + element.width / 2) * sx;
      const cy = (element.y + element.height / 2) * sy;
      scaleContent(doc, id, factor);
      element.x = cx - element.width / 2;
      element.y = cy - element.height / 2;
    }
  }
  // On a board the pages to the right keep their distance.
  const rects = doc.board ? boardRects(doc) : null;
  if (width === doc.format.width && height === doc.format.height) {
    delete page.width;
    delete page.height;
  } else {
    page.width = width;
    page.height = height;
  }
  if (rects && width !== old.width) {
    const before = rects[pageIndex]!;
    const edge = before.x + before.width;
    doc.pages.forEach((other, index) => {
      const rect = rects[index]!;
      if (index === pageIndex) {
        other.x = Math.round(rect.x);
        other.y = Math.round(rect.y);
        return;
      }
      const sameRow = rect.y < before.y + before.height && rect.y + rect.height > before.y;
      other.x = Math.round(sameRow && rect.x >= edge - 1 ? rect.x + (width - old.width) : rect.x);
      other.y = Math.round(rect.y);
    });
  }
}

/** Moves elements (page roots) to another page; `shift` converts their positions. */
export function moveToPage(
  doc: SceneDocument,
  ids: readonly string[],
  targetIndex: number,
  shift: { x: number; y: number },
): void {
  const target = doc.pages[targetIndex];
  if (!target) return;
  const parents = buildParentMap(doc);
  for (const id of ids) {
    const parent = parents.get(id);
    const element = doc.elements[id];
    if (!element || parent?.kind !== "page" || parent.pageIndex === targetIndex) continue;
    const from = doc.pages[parent.pageIndex];
    if (!from) continue;
    from.children = from.children.filter((child) => child !== id);
    target.children.push(id);
    element.x += shift.x;
    element.y += shift.y;
  }
}

export function duplicatePage(doc: SceneDocument, pageIndex: number): number {
  const page = doc.pages[pageIndex];
  if (!page) return -1;
  const data: ClipboardData = { roots: [...page.children], elements: {} };
  for (const id of page.children) {
    for (const descendant of subtreeIds(doc, id)) {
      data.elements[descendant] = clone(doc.elements[descendant]!);
    }
  }
  const copy: ScenePage = {
    id: createId("page"),
    background: clone(page.background),
    children: [],
    ...(page.name ? { name: `${page.name} (Kopie)`.slice(0, 100) } : {}),
    ...(page.notes ? { notes: page.notes } : {}),
    ...(page.width !== undefined ? { width: page.width } : {}),
    ...(page.height !== undefined ? { height: page.height } : {}),
  };
  if (doc.board && supportsBoard(doc)) {
    Object.assign(copy, freeBoardSpot(doc, pageIndex, pageSize(doc, page)));
  }
  doc.pages.splice(pageIndex + 1, 0, copy);
  pasteElements(doc, pageIndex + 1, data);
  return pageIndex + 1;
}

export function removePage(doc: SceneDocument, pageIndex: number): void {
  const page = doc.pages[pageIndex];
  if (!page || doc.pages.length <= 1) return;
  for (const id of page.children) {
    for (const descendant of subtreeIds(doc, id)) delete doc.elements[descendant];
  }
  doc.pages.splice(pageIndex, 1);
}

export function movePage(doc: SceneDocument, from: number, to: number): void {
  const [page] = doc.pages.splice(from, 1);
  if (page) doc.pages.splice(Math.max(0, Math.min(to, doc.pages.length)), 0, page);
}

/** Drops elements that are not reachable from any page (defensive clean-up). */
export function removeOrphans(doc: SceneDocument): number {
  const reachable = new Set<string>();
  for (const page of doc.pages) {
    for (const id of page.children) {
      for (const descendant of subtreeIds(doc, id, [])) reachable.add(descendant);
    }
  }
  let removed = 0;
  for (const id of Object.keys(doc.elements)) {
    if (!reachable.has(id)) {
      delete doc.elements[id];
      removed++;
    }
  }
  return removed;
}
